import React from 'react'
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, Font, PDFViewer } from '@react-pdf/renderer'
import { Person } from '../components/person-form'
import { ContactPerson } from '../components/contact-form'
import { ServiceGiver } from '../components/service-giver-form'
import { Doctor } from '../components/doctor-form'
import { RodoPage } from './rodo-page'

export interface DoctorPdfProps {
  doctor?: {
    person: Person,
    contact1?: ContactPerson;
    contact2?: ContactPerson;
    serviceGiver: ServiceGiver
    doctor: Doctor;
  };
}

Font.register({
  family: 'Roboto',
  src: 'DejaVuSans.ttf'
})

Font.register({
  family: 'small',
  src: 'DejaVuSans.ttf'
})

Font.register({
  family: 'Bold',
  src: 'DejaVuSans-Bold.ttf'
})

Font.register({
  family: 'Italic',
  src: 'DejaVuSans-Oblique.ttf'
})

const styles = StyleSheet.create({
  view: {
    display: 'flex',
    flexDirection: 'row',
    borderLeft: '1 solid black',
    borderRight: '1 solid black'
  },
  standaloneHeader: {
    paddingTop: 8,
    paddingLeft: 5
  },
  body: {
    paddingTop: 35,
    paddingBottom: 30,
    paddingHorizontal: 35,
    fontFamily: 'Roboto',
    fontSize: 10,
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'stretch',
    textAlign: 'justify'
  },
  header: {
    fontSize: 10,
    marginBottom: 5,
    textAlign: 'center',
    fontFamily: 'Bold',
    width: '100%',
  },
  text2: {
    textTransform: 'uppercase',
    fontSize: 10,
    backgroundColor: '#7F7F7F',
    padding: 6,
    fontFamily: 'Bold',
    borderRight: '1 solid black',
    borderBottom: '1 solid black',
    borderLeft: '1 solid black',
    borderTop: '1 solid black'
  },
  fieldHeader: {
    backgroundColor: '#E7E6E6',
  },
  bold: {
    fontFamily: 'Bold',
  },
  italic: {
    fontFamily: 'Italic'
  },
  text3: {
    padding: 5,
    flex: 2
  },
  text4: {
    padding: 5,
    flex: 2,
    backgroundColor: 'white'
  },
  text6: {
    padding: 3,
    flex: 2,
    backgroundColor: 'white'
  },
  text5: {
    paddingTop: 8,
    paddingLeft: 5,
    flex: 1,
  },
   text7: {
    paddingTop: 8,
    paddingLeft: 5,
    flex: 4,
  },
  text: {
    fontSize: 15,
    padding: 5,
    height: 40,
    border: '1 solid black'
  },
  stringDate: {
    borderBottom: '1 dashed black',
  },
  stringDateView: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginLeft: 45,
  },
  charCell: {
    flex: 1,
    borderRight: '1 solid black',
    paddingTop: 4,
    paddingBottom: 4,
    textAlign: 'center'
  },
  sub: {
    fontFamily: 'small'
  },
  descriptionTag: { fontSize: 5, width: 20 },
  descriptionText: { fontSize: 8 }
});


const DoctorPdf: React.FC<DoctorPdfProps> = function (props) {
  let info = props.doctor
  if (!info) {
    info = {
      person: {
        addressInfo: {
          house: '',
          postalAndCity: '',
          street: ''
        },
        personalInfo: {
          dateOfBirth: new Date(),
          firstName: '',
          lastName: '',
          pesel: '           '
        },
        phone: {}
      },
      doctor: {
        choice: {
          choiceThisYear: ''
        },
        name: ''
      },
      serviceGiver: {
        address: '',
        choice: {
          choiceThisYear: ''
        },
        name: ''
      }
    }
  }

  const date = info.person.personalInfo.dateOfBirth || new Date()
  const StringDate = () => {
    const Dotted = (props: any) => {
      console.log(props)
      return <Text style={styles.stringDate}>{props.children}</Text>
    }
    return (
      <View style={styles.stringDateView}>
        <Dotted>{date.getDate().toString().padStart(2, '0')}</Dotted>
        <Text>-</Text>
        <Dotted>{(date.getMonth() + 1).toString().padStart(2, '0')}</Dotted>
        <Text>-</Text>
        <Dotted>{date.getFullYear()}</Dotted>
      </View>
    )
  }

  function getPersonPhone (): string[] {
    if (!info || !info.person.phone.phone) {
      return ['', '', '', '', '', '', '', '', '', '', '', '', '']
    }
    return info?.person.phone.phone.padStart(13, ' ').split('')
  }
  const todayDate = new Date().getDate().toString().padStart(2, '0') + '-' + (new Date().getMonth() + 1).toString().padStart(2, '0') + '-' + new Date().getFullYear()
  return (
    <Document>
      <Page style={styles.body}>
        <View style={styles.header}>
          <View><Text>DEKLARACJA WYBORU ŚWIADCZENIODAWCY UDZIELAJĄCEGO ŚWIADCZEŃ</Text></View>
          <View><Text>ZAKRESU PODSTAWOWEJ OPIEKI ZDROWOTNEJ ORAZ LEKARZA</Text></View>
          <View><Text>PODSTAWOWEJ OPIEKI ZDROWOTNEJ</Text></View>
        </View>
        <Text style={styles.text2}>I. DANE DOTYCZĄCE ŚWIADCZENIOBIORCY</Text>
        <View style={{ ...styles.view, ...styles.bold, borderBottom: '1 solid black' }}>
          <Text style={{ ...styles.text3, ...styles.fieldHeader, borderRight: '1 solid black' }}>1. Imię</Text>
          <Text style={{ ...styles.text3, ...styles.fieldHeader }}>2. Nazwisko</Text>
        </View>
        <View style={{ ...styles.view, borderBottom: '1 solid black' }}>
          <Text style={{ ...styles.text3, borderRight: '1 solid black' }}>{info.person.personalInfo.firstName}</Text>
          <Text style={{ ...styles.text3 }}>{info.person.personalInfo.lastName}</Text>
        </View>
        <View style={{ ...styles.view, ...styles.bold, borderBottom: '1 solid black' }}>
          <Text style={{ ...styles.text4, ...styles.fieldHeader, borderRight: '1 solid black' }}>3. Data urodzenia</Text>
          <Text style={{ ...styles.text7, ...styles.fieldHeader, flex: 4 }}>4. Numer PESEL, a w przypadku jego braku seria i numer{'\n'}dokumentu potwierdzającego tożsamość</Text>
        </View>
        <View style={{ ...styles.view, borderBottom: '1 solid black' }}>
          <View style={{ ...styles.text3, borderRight: '1 solid black' }}>
            <StringDate />
            <View style={{ marginLeft: 30 }}>
              <Text>dzień - miesiąc - rok</Text>
            </View>
          </View>
          <View style={{ flex: 4, display: 'flex', flexDirection: 'row', paddingLeft: 5}}>
            {info.person.personalInfo.pesel.split('').map((c, i)=> <Text key={i} style={{ ...styles.charCell, borderRight: i === 10 ? 'none': '1 solid black' }}>{ c }</Text>)}
          </View>
        </View>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black' }}>
          <Text style={{ ...styles.bold }}>5. Adres miejsca zamieszkania</Text>
          <View style={{ ...styles.view, borderRight: 'none', backgroundColor: 'white', borderBottom: '1 solid black' }}>
            <Text style={{ ...styles.text3, borderTop: '1 solid black', borderRight: '1 solid black' }}>5A. <Text style={styles.italic}>Ulica</Text></Text>
            <Text style={{ ...styles.text3, borderTop: '1 solid black', flex: 1, borderRight: '1 solid black' }}>5B. <Text style={styles.italic}>Numer{'\n'}domu/mieszkania</Text></Text>
            <Text style={{ ...styles.text3, borderTop: '1 solid black' }}>5C. <Text style={styles.italic}>Kod pocztowy i miejscowość</Text></Text>
          </View>
          <View style={{ ...styles.view, borderRight: 'none', backgroundColor: 'white' }}>
            <Text style={{ ...styles.text3, borderBottom: 'none', borderRight: '1 solid black', paddingBottom: 10, borderLeft: 'none' }}>{info.person.addressInfo.street}</Text>
            <Text style={{ ...styles.text3, borderBottom: 'none', borderRight: '1 solid black', flex: 1 }}>{info.person.addressInfo.house}</Text>
            <Text style={{ ...styles.text3, borderBottom: 'none' }}>{info.person.addressInfo.postalAndCity}</Text>
          </View>
        </View>
        <View style={{ ...styles.view, borderBottom: '1 solid black' }}>
  <Text style={{ ...styles.text3, ...styles.fieldHeader, borderRight: '1 solid black' }}><Text style={styles.bold}>6. Numer telefonu</Text>{'\n'}<Text style={styles.italic}>(pole nieobowiązkowe)</Text></Text>
          <View style={{ flex: 5, display: 'flex', flexDirection: 'row' }}>
            {getPersonPhone().map((c, i)=> <Text key={i} style={{ ...styles.charCell, borderRight: i === 12 ? 'none': '1 solid black' }}>{ c }</Text>)}
          </View>
        </View>
        <View style={{ ...styles.view, borderBottom: '1 solid black' }}>
          <Text style={{ ...styles.text3, ...styles.fieldHeader, flex: 66, paddingLeft: 5, borderRight: '1 solid black' }}><Text style={styles.bold}>7. Adres email</Text> <Text style={styles.italic}>(pole nieobowiązkowe)</Text></Text>
          <Text style={{ ...styles.text3, flex: 87 }}>{info?.person.phone.email}</Text>
        </View>
        <View>
          <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
            <Text style={{ marginRight: 5, marginBottom: 5 }}><Text style={styles.bold}>8. Dane dotyczące przedstawiciela ustawowego</Text> <Text style={styles.italic}>(w przypadku gdy świadczeniobiorcą jest osoba małoletnia {'\n'}lub całkowicie ubezwłasnowolniona</Text><Text style={styles.sub}>¹⁾</Text>)</Text>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none' }}>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8A. <Text style={styles.italic}>Imię</Text></Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8B. <Text style={styles.italic}>Nazwisko</Text></Text>
              <Text style={{ ...styles.text4 }}>8C. <Text style={styles.italic}>Numer telefonu{'\n'}(pole nieobowiązkowe)</Text></Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none' }}>
              <Text style={{ ...styles.text4, paddingBottom: 10, borderRight: '1 solid black' }}>{info?.contact1?.personalInfo.firstName}</Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>{info?.contact1?.personalInfo.lastName}</Text>
              <Text style={styles.text4}>{info?.contact1?.personalInfo.phone}</Text>
            </View>
            <View style={{ ...styles.view, padding: 5, borderRight: 'none', borderTop: '1 solid black' }}>
              <Text style={styles.italic}>Adres miejsca zamieszkania</Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none', }}>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8D. <Text style={styles.italic}>Ulica</Text></Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black', flex: 1 }}>8E. <Text style={styles.italic}>Numer{'\n'}domu/mieszkania</Text></Text>
              <Text style={{ ...styles.text4 }}>8F. <Text style={styles.italic}>Kod pocztowy i miejscowość</Text></Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none', }}>
              <Text style={{ ...styles.text4, paddingBottom: 10, borderRight: '1 solid black' }}>{info?.contact1?.addressInfo.street}</Text>
              <Text style={{ ...styles.text4, flex: 1, borderRight: '1 solid black' }}>{info?.contact1?.addressInfo.house}</Text>
              <Text style={{ ...styles.text4 }}>{info?.contact1?.addressInfo.postalAndCity}</Text>
            </View>
            <View style={{ height: 20, borderTop: '1 solid black' }}></View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none' }}>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8G. <Text style={styles.italic}>Imię</Text></Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8H. <Text style={styles.italic}>Nazwisko</Text></Text>
              <Text style={{ ...styles.text4 }}>8I. <Text style={styles.italic}>Numer telefonu{'\n'}(pole nieobowiązkowe)</Text></Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none' }}>
              <Text style={{ ...styles.text4, paddingBottom: 10, borderRight: '1 solid black' }}>{info?.contact2?.personalInfo.firstName}</Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>{info?.contact2?.personalInfo.lastName}</Text>
              <Text style={styles.text4}>{info?.contact2?.personalInfo.phone}</Text>
            </View>
            <View style={{ ...styles.view, padding: 5, borderRight: 'none', borderTop: '1 solid black' }}>
              <Text style={styles.italic}>Adres miejsca zamieszkania</Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none', }}>
              <Text style={{ ...styles.text4, borderRight: '1 solid black' }}>8J. <Text style={styles.italic}>Ulica</Text></Text>
              <Text style={{ ...styles.text4, borderRight: '1 solid black', flex: 1 }}>8K. <Text style={styles.italic}>Numer{'\n'}domu/mieszkania</Text></Text>
              <Text style={{ ...styles.text4 }}>8L. <Text style={styles.italic}>Kod pocztowy i miejscowość</Text></Text>
            </View>
            <View style={{ ...styles.view, borderTop: '1 solid black', borderRight: 'none', }}>
              <Text style={{ ...styles.text4, paddingBottom: 10, borderRight: '1 solid black' }}>{info?.contact2?.addressInfo.street}</Text>
              <Text style={{ ...styles.text4, flex: 1, borderRight: '1 solid black' }}>{info?.contact2?.addressInfo.house}</Text>
              <Text style={{ ...styles.text4 }}>{info?.contact2?.addressInfo.postalAndCity}</Text>
            </View>
          </View>
        </View>
      </Page>
      <Page style={styles.body}>
        <Text style={styles.text2}>II. DANE DOTYCZĄCE ŚWIADCZENIODAWCY</Text>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
  <Text style={{ marginRight: 5 }}><Text style={styles.bold}>9.</Text> Na podstawie art. 9 ust. 1 ustawy z dnia 27 października 2017 r. o podstawowej opiece{'\n'}zdrowotnej (Dz. U. z 2020 r. poz. 172) deklaruję wybór:</Text>
          <View style={{ ...styles.view, borderBottom: 'none', borderRight: 'none' }}>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black' }}>9A. <Text style={styles.italic}>Nazwa (firma){'\n'}świadczeniodawcy</Text></Text>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', flex: 5 }}>{info.serviceGiver.name}</Text>
          </View>
          <View style={{ ...styles.view, borderBottom: 'none', borderRight: 'none' }}>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black' }}>9B. <Text style={styles.italic}>Adres siedziby{'\n'}świadczeniodawcy</Text></Text>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', flex: 5 }}>{info.serviceGiver.address}</Text>
          </View>
        </View>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
          <Text><Text style={styles.bold}>10.</Text> W bieżącym roku dokonuję wyboru:<Text style={styles.sub}>²⁾</Text></Text>
          <View style={{ ...styles.view, borderBottom: 'none', borderRight: 'none' }}>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black' }}>{ info.serviceGiver.choice.choiceThisYear === 'Po raz pierwszy lub po raz drugi' ? '☒' : '☐' } po raz pierwszy lub po raz drugi</Text>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black', marginRight: 5 }}>{ info.serviceGiver.choice.choiceThisYear === 'Po raz kolejny' ? '☒' : '☐' } po raz kolejny<Text style={styles.sub}>³⁾</Text></Text>
          </View>
        </View>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: 'none', borderRight: '1 solid black'  }}>
          <Text style={{ marginRight: 5 }}><Text style={styles.bold}>11.</Text> W przypadku dokonania wyboru po raz trzeci lub kolejny w bieżącym roku, należy wskazać, czy powodem dokonania wyboru jest:</Text>
          <View style={{ ...styles.view, backgroundColor: 'white', paddingRight: 5, marginRight: 5, borderTop: '1 solid black', borderBottom: 'none', display: 'flex', flexDirection: 'column', alignContent: "stretch" }}>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none' }}>{ info.serviceGiver.choice.reasonOfChange?.home || info.serviceGiver.choice.reasonOfChange?.serviceStopped || info.serviceGiver.choice.reasonOfChange?.becameAdult || info.serviceGiver.choice.reasonOfChange?.otherFromchoice ? '☒' : '☐' }    okoliczność określona w art. 9 ust. 5 ustawy z dnia 27 października 2017 r. o podstawowej{'\n'}opiece zdrowotnej:</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.serviceGiver.choice.reasonOfChange?.home ? '●' : '○'}  zmiana miejsca zamieszkania</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.serviceGiver.choice.reasonOfChange?.serviceStopped ? '●' : '○'}   zaprzestanie udzielania świadczeń opieki zdrowotnej przez wybranego świadczeniodawcę, lekarza podstawowej opieki zdrowotnej u wybranego świadczeniodawcy</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.serviceGiver.choice.reasonOfChange?.becameAdult ? '●' : '○'}   osiągnięcie 18. roku życia przez świadczeniobiorcę, gdy lekarzem podstawowej opieki zdrowotnej jest lekarz posiadający specjalizację I lub II stopnia lub tytuł specjalisty w dziedzinie pediatrii</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.serviceGiver.choice.reasonOfChange?.otherFromchoice ? '●' : '○'}   z innych przyczyn powstałych po stronie świadczeniodawcy (podać jakich)</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', borderBottom: '1 dotted black', marginLeft: 15, width: '90%' }}>{ info.serviceGiver.choice.reasonOfChange?.lawReason || ''}</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none' }}>{ info.serviceGiver.choice.reasonOfChange?.other ? '☒' : '☐' }    inna okoliczność </Text>
          </View>
        </View>
        <Text style={styles.text2}>III. DANE DOTYCZĄCE LEKARZA PODSTAWOWEJ OPIEKI ZDROWOTNEJ</Text>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', paddingRight: 5, borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
          <Text><Text style={styles.bold}>12.</Text> Na podstawie art. 9 ust. 2 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej deklaruję wybór:<Text style={styles.sub}>⁴⁾</Text></Text>
          <View style={{ ...styles.view, borderBottom: 'none', borderRight: 'none' }}>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black', ...styles.italic }}>Imię i nazwisko{'\n'}lekarza podstawowej{'\n'}opieki zdrowotnej</Text>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black', flex: 5 }}>{info.doctor.name}</Text>
          </View>
        </View>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
          <Text><Text style={styles.bold}>13.</Text> W bieżącym roku dokonuję wyboru:<Text style={styles.sub}>²⁾</Text></Text>
          <View style={{ ...styles.view, borderBottom: 'none', borderRight: 'none' }}>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black' }}>{ info.serviceGiver.choice.choiceThisYear === 'Po raz pierwszy lub po raz drugi' ? '☒' : '☐' } po raz pierwszy lub raz drugi</Text>
            <Text style={{ ...styles.text4, borderTop: '1 solid black', borderRight: '1 solid black', marginRight: 5 }}>{ info.serviceGiver.choice.choiceThisYear === 'Po raz kolejny' ? '☒' : '☐' } po raz kolejny<Text style={styles.sub}>³⁾</Text></Text>
          </View>
        </View>
        <View style={{ ...styles.fieldHeader, ...styles.standaloneHeader, borderLeft: '1 solid black', borderBottom: '1 solid black', borderRight: '1 solid black'  }}>
          <Text style={{ marginRight: 5 }}><Text style={styles.bold}>14.</Text> W przypadku dokonania wyboru po raz trzeci lub kolejny w bieżącym roku, należy wskazać, czy powodem dokonania wyboru jest:</Text>
          <View style={{ ...styles.view, backgroundColor: 'white', marginRight: 5, paddingRight: 5, borderTop: '1 solid black', borderBottom: 'none', display: 'flex', flexDirection: 'column', alignContent: "stretch" }}>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none' }}>{ info.doctor.choice.reasonOfChange?.home || info.doctor.choice.reasonOfChange?.serviceStopped|| info.doctor.choice.reasonOfChange?.becameAdult || info.doctor.choice.reasonOfChange?.otherFromchoice ? '☒' : '☐' }    okoliczność określona w art. 9 ust. 5 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej:</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.doctor.choice.reasonOfChange?.home ? '●' : '○'}   zmiana miejsca zamieszkania</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.doctor.choice.reasonOfChange?.serviceStopped ? '●' : '○'}   zaprzestanie udzielania świadczeń opieki zdrowotnej przez wybranego świadczeniodawcę, lekarza podstawowej opieki zdrowotnej u wybranego świadczeniodawcy</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.doctor.choice.reasonOfChange?.becameAdult ? '●' : '○'}   osiągnięcie 18. roku życia przez świadczeniobiorcę, gdy lekarzem podstawowej opieki zdrowotnej jest lekarz posiadający specjalizację I lub II stopnia lub tytuł specjalisty w dziedzinie pediatrii</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', marginLeft: 15 }}>{ info.doctor.choice.reasonOfChange?.otherFromchoice ? '●' : '○'}   z innych przyczyn powstałych po stronie świadczeniodawcy (podać jakich)</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none', borderBottom: '1 dotted black', marginLeft: 15 }}>{ info.doctor.choice.reasonOfChange?.lawReason || ''}</Text>
            <Text style={{ ...styles.text6, borderTop: '1 solid black', border: 'none' }}>{ info.doctor.choice.reasonOfChange?.other ? '☒' : '☐' }    inna okoliczność </Text>
          </View>
        </View>
        <View style={{ display: "flex", flexDirection: 'row', border: '1 solid black', borderTop: 'none'}}>
          <View style={{ display: 'flex', flexDirection: 'column', alignContent: "center", borderRight: '1 solid black', textAlign: 'center', height: 40, paddingTop: 10, flex: 3 }}>
            <Text>{todayDate}</Text>
            <Text>(data)</Text>
          </View>
          <View style={{ display: 'flex', flexDirection: 'column', alignContent: "stretch", textAlign: 'center', paddingTop: 10, flex: 5, height: 40 }}>
            <Text  style={{ borderBottom: '1 dotted black' }}>                 </Text>
            <Text>(podpis świadczeniobiorcy lub przedstawiciela ustawowego)<Text style={styles.sub}>⁵⁾</Text></Text>
          </View>
        </View>
      </Page>
      <Page style={styles.body}>
        <View>
          <Text style={{ fontWeight: 'bold', paddingTop: 5 }}>OBJAŚNIENIA:</Text>
          <View style={{ display: 'flex', flexDirection: 'row', alignContent: 'stretch' }}>
            <Text style={styles.descriptionTag}>1)</Text>
            <Text style={styles.descriptionText}>Deklaracje złożone przez przedstawicieli ustawowych w imieniu osób małoletnich po uzyskaniu przez nie pełnoletności zachowują ważność do czasu wyboru innego lekarza podstawowej opieki zdrowotnej, a w sytuacji gdy lekarzem podstawowej opieki zdrowotnej jest lekarz posiadający specjalizację I lub II stopnia lub tytuł specjalisty w dziedzinie pediatrii, deklaracje zachowują ważność w zakresie wyboru świadczeniodawcy do czasu wyboru innego lekarza podstawowej opieki zdrowotnej lub wyboru nowego świadczeniodawcy (art. 6 ust. 3 oraz art. 10 ust. 7 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej).</Text>
          </View>
          <View break style={{ display: 'flex', flexDirection: 'row', alignContent: 'stretch' }}>
            <Text style={styles.descriptionTag}>2)</Text>
            <Text style={styles.descriptionText}>Świadczeniobiorca ma prawo bezpłatnego wyboru świadczeniodawcy lub lekarza podstawowej opieki zdrowotnej nie częściej niż dwa razy w roku kalendarzowym, a w przypadku każdej kolejnej zmiany wnosi opłatę w wysokości 80 zł. Świadczeniobiorca nie ponosi opłaty w przypadku zmiany swojego miejsca zamieszkania lub w przypadku zaprzestania udzielania świadczeń opieki zdrowotnej przez wybranego świadczeniodawcę, lekarza podstawowej opieki zdrowotnej u wybranego świadczeniodawcy, lub w sytuacji gdy świadczeniobiorca, którego lekarzem podstawowej opieki zdrowotnej jest lekarz posiadający specjalizację I lub II stopnia lub tytuł specjalisty w dziedzinie pediatrii, osiągnął 18. rok życia, lub z innych przyczyn powstałych po stronie świadczeniodawcy (art. 9 ust. 4 i 5 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej).</Text>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', alignContent: 'stretch' }}>
            <Text style={styles.descriptionTag}>3)</Text>
            <Text style={styles.descriptionText}>W przypadku wyboru trzeciego i kolejnego należy podać informację, czy powodem dokonania wyboru jest okoliczność określona w art. 9 ust. 5 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej, tzn. zmiana miejsca zamieszkania świadczeniobiorcy lub zaprzestanie udzielania świadczeń opieki zdrowotnej przez wybranego świadczeniodawcę, lekarza podstawowej opieki zdrowotnej u wybranego świadczeniodawcy lub zmiana lekarza podstawowej opieki zdrowotnej, w sytuacji gdy świadczeniobiorca, którego lekarzem podstawowej opieki zdrowotnej jest lekarz posiadający specjalizację I lub II stopnia lub tytuł specjalisty w dziedzinie pediatrii, osiągnął 18. rok życia, lub z innych przyczyn powstałych po stronie świadczeniodawcy.</Text>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', alignContent: 'stretch' }}>
            <Text style={styles.descriptionTag}>4)</Text>
            <Text style={styles.descriptionText}>Świadczeniobiorca może wybrać lekarza podstawowej opieki zdrowotnej, pielęgniarkę podstawowej opieki zdrowotnej lub położną podstawowej opieki zdrowotnej u tego samego świadczeniodawcy, u różnych świadczeniodawców albo będących świadczeniodawcami (art. 9 ust. 3 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej).</Text>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', alignContent: 'stretch' }}>
            <Text style={styles.descriptionTag}>5)</Text>
            <Text style={styles.descriptionText}>Wypełnia się w przypadku deklaracji składanej w postaci papierowej lub postaci elektronicznej, o których mowa w art. 10 ust. 1 pkt 1 albo 2 ustawy z dnia 27 października 2017 r. o podstawowej opiece zdrowotnej.</Text>
          </View>
        </View>
      </Page>
      <RodoPage />
    </Document>
  )
}

export const DownloadPdfDoctorLink: React.FC<DoctorPdfProps> = function DownloadDoctorPdfLink (props) {
  let filename = 'Deklaracja.pdf'
  if (props.doctor) {
    filename = props.doctor.person.personalInfo.pesel.substr(0, 9) + '.pdf'
  }
  return (
    <div>
      <PDFDownloadLink document={<DoctorPdf doctor={props.doctor} />} fileName={filename} style={{ color: 'inherit', textDecoration: 'none' }}>
        {({ blob, url, loading, error }) => (loading ? 'TRWA GENEROWANIE...' : 'POBIERZ DEKLARACJĘ')}
      </PDFDownloadLink>
    </div>
  )
}

export const DoctorPdfViewer: React.FC = function () {
  return (
    <PDFViewer style={{ width: '100%', height: '1000px' }}>
      <DoctorPdf />
    </PDFViewer>
  )
}
